import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { getAuthHeaders } from '@/identity.js';

/**
 * Client for the workspace artifacts registry (`cti-workspace-service`, reached via the gateway at
 * `/v1/workspaces/:workspaceId/artifacts`). Global-workspaces: a workspace has a shared file store
 * (agent-generated files, user uploads, deck links). Each file carries one of four read scopes —
 * `restricted`, `conversation`, `group` or `workspace` (see {@link ArtifactVisibility}) — and the
 * creator or a workspace owner moves it between them through the share endpoint. Delete is gated to
 * the creator or a workspace owner server-side.
 *
 * The registry used to live in `cti-conversation-service` under `/v1/workspace-artifacts/:workspaceId`.
 * That prefix still answers through a forwarder so a cached bundle keeps working, but every call from
 * here goes to the new workspace-scoped route.
 *
 * REST is snake_case on the wire; this maps it to the camelCase the SPA uses. When a field crosses
 * this boundary the exact snake_case key must match the service DTO — the service's `ValidationPipe`
 * silently drops any key it doesn't declare. `source` / `visibility` are OPEN unions: a newer service
 * may send a value outside the known set, so consumers must fall back to a safe default rather than
 * index a lookup map blindly (see CLAUDE.md).
 */

const API_BASE = '/v1/workspaces';

/** Known artifact origins. Treat as OPEN — a newer service can send an unknown value. */
export type ArtifactSource = 'agent_generated' | 'user_upload' | 'deck_link';

/**
 * Read scope of an artifact. `restricted` = creator only; `conversation` = whoever can see
 * the chat the file was born in, which a chat-born file inherits; `workspace` = all members.
 */
export type ArtifactVisibility =
  'restricted' | 'conversation' | 'group' | 'workspace';

/** A workspace artifact as the SPA consumes it (camelCase). `source`/`visibility` are kept as raw
 * strings (open union) so an unknown value never breaks parsing — the UI resolves a label/fallback.
 * There is no S3 key here on purpose: the service never sends one, bytes only come from the content
 * route. */
export interface WorkspaceArtifact {
  id: string;
  workspaceId: string;
  conversationId: string | null;
  createdBy: string;
  name: string;
  contentType: string;
  size: number;
  source: string;
  visibility: string;
  createdAt: string;
  status?: 'active' | 'archived';
  archivedAt?: string | null;
  /** Number of the newest stored revision — what the editor and the version history read. */
  headRevision: number;
  updatedAt: string;
  updatedBy: string;
  /**
   * Where this file came from, when it came from somebody else's share. Denormalised onto the copy on
   * purpose: the share gets revoked and the original gets deleted, and the answer to "how do I have this
   * file" has to outlive both. Optional because only the artifact detail view carries them, and older
   * rows carry none.
   *
   * `copiedFromWorkspaceId` is here because the server sends it, NOT because the interface may show it —
   * that name is usually a client's or a project's. See `features/artifacts/artifact-provenance.ts`.
   */
  copiedFromArtifactId?: string | null;
  copiedFromWorkspaceId?: string | null;
  copiedFromRevision?: number | null;
  copiedFromUserId?: string | null;
  /** Display name of the person who shared it, resolved by the service; null when it could not be. */
  copiedFromUserName?: string | null;
}

/** The snake_case shape the service returns per artifact. The serializer can send a null
 * `content_type`/`size` (omitted on the row); {@link fromWire} coerces a null content type to `''` and a
 * null size to `-1` (unknown), which the UI renders as `—` rather than a misleading `0 B`.
 *
 * The revision fields are optional because the retired `/v1/workspace-artifacts` forwarder can answer a
 * row without them; {@link fromWire} fills them from the created values. */
interface WorkspaceArtifactWire {
  id: string;
  workspace_id: string;
  conversation_id: string | null;
  created_by: string;
  name: string;
  content_type: string | null;
  size: number | null;
  source: string;
  visibility: string;
  created_at: string;
  status?: string;
  archived_at?: string | null;
  head_revision?: number | null;
  updated_at?: string | null;
  updated_by?: string | null;
  copied_from_artifact_id?: string | null;
  copied_from_workspace_id?: string | null;
  copied_from_revision?: number | null;
  copied_from_user_id?: string | null;
  copied_from_user_name?: string | null;
}

/**
 * One entry of an artifact's version history as the SPA consumes it (camelCase). `revision` counts from
 * 1 and only ever goes up: a save appends one and a restore appends a copy of an older one, so the head
 * revision is also how many versions the file has. A legacy artifact registered before this registry
 * existed answers with exactly one synthesized revision 1.
 *
 * `size` uses the same `-1` unknown sentinel as {@link WorkspaceArtifact}, so `formatBytes` renders `—`
 * rather than a misleading `0 B`.
 */
export interface ArtifactRevision {
  revision: number;
  contentType: string;
  size: number;
  createdBy: string;
  createdAt: string;
}

/** The snake_case shape the service returns per revision. Both facts can be null on an older row. */
interface ArtifactRevisionWire {
  revision: number;
  content_type: string | null;
  size: number | null;
  created_by: string;
  created_at: string;
}

/** Optional list filters. `conversationId` maps to the `conversation_id` query key. */
export interface WorkspaceArtifactFilters {
  q?: string;
  type?: string;
  conversationId?: string;
  status?: 'archived';
}

/**
 * Maps one wire row to the SPA shape. A row with no revision fields falls back to "revision 1, never
 * touched since it was created": every stored artifact has at least one revision, and under-reporting
 * the head is the safe direction — a write that sends a stale expected revision is rejected by the
 * service rather than clobbering someone else's newer revision.
 */
function fromWire(wire: WorkspaceArtifactWire): WorkspaceArtifact {
  return {
    id: wire.id,
    workspaceId: wire.workspace_id,
    conversationId: wire.conversation_id,
    createdBy: wire.created_by,
    name: wire.name,
    contentType: wire.content_type ?? '',
    size: typeof wire.size === 'number' ? wire.size : -1,
    source: wire.source,
    visibility: wire.visibility,
    createdAt: wire.created_at,
    status:
      wire.archived_at != null || wire.status === 'archived'
        ? 'archived'
        : 'active',
    archivedAt: wire.archived_at ?? null,
    headRevision:
      typeof wire.head_revision === 'number' ? wire.head_revision : 1,
    updatedAt: wire.updated_at ?? wire.created_at,
    updatedBy: wire.updated_by ?? wire.created_by,
    copiedFromArtifactId: wire.copied_from_artifact_id ?? null,
    copiedFromWorkspaceId: wire.copied_from_workspace_id ?? null,
    copiedFromRevision: wire.copied_from_revision ?? null,
    copiedFromUserId: wire.copied_from_user_id ?? null,
    copiedFromUserName: wire.copied_from_user_name ?? null,
  };
}

/** Maps one wire revision to the SPA shape, with the same null coercions {@link fromWire} applies. */
function revisionFromWire(wire: ArtifactRevisionWire): ArtifactRevision {
  return {
    revision: wire.revision,
    contentType: wire.content_type ?? '',
    size: typeof wire.size === 'number' ? wire.size : -1,
    createdBy: wire.created_by,
    createdAt: wire.created_at,
  };
}

/** Collection route of a workspace's artifacts (`/v1/workspaces/:workspaceId/artifacts`). */
function collectionPath(workspaceId: string): string {
  return `${API_BASE}/${encodeURIComponent(workspaceId)}/artifacts`;
}

/** Item route of one artifact (`/v1/workspaces/:workspaceId/artifacts/:id`). */
function itemPath(workspaceId: string, id: string): string {
  return `${collectionPath(workspaceId)}/${encodeURIComponent(id)}`;
}

/**
 * Bytes route of one artifact, optionally pinned to a stored revision. Exported because a chat
 * attachment can point here, and `isAttachmentEndpointUrl` has to recognise the shape this builds —
 * a url it fails to recognise goes straight into `<img src>` and 401s (the route is bearer-authed).
 */
export function workspaceArtifactContentUrl(
  workspaceId: string,
  id: string,
  revision?: number,
): string {
  const base = `${itemPath(workspaceId, id)}/content`;
  return revision === undefined ? base : `${base}?revision=${revision}`;
}

/** True when the artifact is shared with every workspace member (anything else reads as narrower). */
export function isSharedWithWorkspace(artifact: WorkspaceArtifact): boolean {
  return artifact.visibility === 'workspace';
}

/**
 * Request headers for every call. Delegates the bearer to {@link getAuthHeaders} so each request gets
 * a fresh platform JWT — inside Teams it silently re-exchanges an expired token — rather than reading a
 * possibly-stale value from the store.
 */
async function headers(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text.trim()) return `${fallback} (${res.status})`;
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string' && body.message.trim())
      return body.message;
  } catch {
    /* not JSON */
  }
  return text.trim();
}

/**
 * List a workspace's artifacts. The response is wrapped — `{ artifacts: [...] }` — so this reads
 * `body.artifacts`, never a bare array. Optional filters map to `q` / `type` / `conversation_id`.
 */
export async function listWorkspaceArtifacts(
  workspaceId: string,
  filters: WorkspaceArtifactFilters = {},
): Promise<WorkspaceArtifact[]> {
  const params = new URLSearchParams();
  const q = filters.q?.trim();
  if (q) params.set('q', q);
  const type = filters.type?.trim();
  if (type) params.set('type', type);
  const conversationId = filters.conversationId?.trim();
  if (conversationId) params.set('conversation_id', conversationId);
  if (filters.status === 'archived') params.set('status', 'archived');
  const query = params.toString();
  const url = query
    ? `${collectionPath(workspaceId)}?${query}`
    : collectionPath(workspaceId);
  const res = await fetch(url, { headers: await headers() });
  if (!res.ok) throw new Error(await errorMessage(res, 'Failed to load files'));
  const body = (await res.json()) as { artifacts?: WorkspaceArtifactWire[] };
  const rows = body.artifacts ?? [];
  return rows
    .map(fromWire)
    .filter(
      (artifact) =>
        filters.status !== 'archived' || artifact.status === 'archived',
    );
}

/** Fetch one artifact's metadata. */
export async function getWorkspaceArtifact(
  workspaceId: string,
  id: string,
): Promise<WorkspaceArtifact> {
  const res = await fetch(itemPath(workspaceId, id), {
    headers: await headers(),
  });
  if (!res.ok) throw new Error(await errorMessage(res, 'Failed to load file'));
  return fromWire((await res.json()) as WorkspaceArtifactWire);
}

/**
 * Fetch an artifact's bytes as a Blob, the newest revision unless one is named. The content route is
 * bearer-authed (a plain `<img>`/`<iframe>` sub-request can't carry the Authorization header → 401), so
 * callers fetch here and expose an object URL, mirroring the chat-attachment `fetchAttachmentBlob` →
 * object-URL pattern.
 */
export async function fetchWorkspaceArtifactBlob(
  workspaceId: string,
  id: string,
  revision?: number,
): Promise<Blob> {
  const res = await fetch(
    workspaceArtifactContentUrl(workspaceId, id, revision),
    {
      headers: await headers(),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to download file'));
  return res.blob();
}

/**
 * Delete an artifact. Allowed for the creator or a workspace owner server-side. The service answers
 * `200 { deleted: true }` (the retired forwarder answered 204), so any ok status counts — the body is
 * not read. A 404 means it's already gone: an idempotent success, not an error, so a bulk delete or a
 * concurrent edit doesn't surface a spurious failure.
 */
export async function deleteWorkspaceArtifact(
  workspaceId: string,
  id: string,
): Promise<void> {
  const res = await fetch(itemPath(workspaceId, id), {
    method: 'DELETE',
    headers: await headers(),
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(await errorMessage(res, 'Failed to delete file'));
  }
}

async function transitionWorkspaceArtifact(
  workspaceId: string,
  id: string,
  action: 'archive' | 'restore',
): Promise<void> {
  const res = await fetch(`${itemPath(workspaceId, id)}/${action}`, {
    method: 'POST',
    headers: await headers(),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessage(
        res,
        `Failed to ${action === 'archive' ? 'archive' : 'restore'} file`,
      ),
    );
  }
}

export async function archiveWorkspaceArtifact(
  workspaceId: string,
  id: string,
): Promise<void> {
  return transitionWorkspaceArtifact(workspaceId, id, 'archive');
}

export async function restoreWorkspaceArtifact(
  workspaceId: string,
  id: string,
): Promise<void> {
  return transitionWorkspaceArtifact(workspaceId, id, 'restore');
}

/**
 * Change who can read an artifact. Allowed for the creator or a workspace owner; the server rejects
 * sharing in a personal workspace, a `group` with nobody in it, and a list of people sent alongside any
 * other visibility. Returns the updated row.
 *
 * `userIds` is the WHOLE list, not a delta, and it rides only a `group` save — the key on the wire is
 * `user_ids`, and the service drops an undeclared key with no error, so the spelling is pinned by a
 * test. Omitting it on a `group` save keeps whatever list the server already holds.
 */
export async function setWorkspaceArtifactVisibility(
  workspaceId: string,
  id: string,
  visibility: ArtifactVisibility,
  userIds?: readonly string[],
): Promise<WorkspaceArtifact> {
  const res = await fetch(itemPath(workspaceId, id), {
    method: 'PATCH',
    headers: await headers(),
    body: JSON.stringify({
      visibility,
      ...(visibility === 'group' && userIds ? { user_ids: [...userIds] } : {}),
    }),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to update sharing'));
  return fromWire((await res.json()) as WorkspaceArtifactWire);
}

/** One person who can read an artifact, as the SPA consumes it. `reason` is an OPEN union. */
export interface ArtifactAccessEntry {
  userId: string;
  name: string | null;
  email: string | null;
  role: string;
  reason: string;
}

/**
 * Everyone who can read one artifact, and why. The server builds this from the workspace member
 * directory and re-checks it through the same gate every read goes through, so it is the truth rather
 * than a restatement of the visibility — somebody named on a file who has left the workspace is absent.
 */
export async function fetchArtifactAccess(
  workspaceId: string,
  id: string,
): Promise<ArtifactAccessEntry[]> {
  const res = await fetch(`${itemPath(workspaceId, id)}/access`, {
    headers: await headers(),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load who has access'));
  const body = (await res.json()) as {
    access?: {
      user_id: string;
      name: string | null;
      email: string | null;
      role: string;
      reason: string;
    }[];
  };
  return (body.access ?? []).map((entry) => ({
    userId: entry.user_id,
    name: entry.name,
    email: entry.email,
    role: entry.role,
    reason: entry.reason,
  }));
}

/**
 * One artifact, including the people a `group` file names. The list route leaves `shared_with` off, so
 * this is what seeds the share dialog's picker — and reading it fresh on every open is what stops a
 * stale set from silently replacing a list somebody else just changed.
 */
export async function fetchWorkspaceArtifact(
  workspaceId: string,
  id: string,
): Promise<WorkspaceArtifact & { sharedWith: string[] | null }> {
  const res = await fetch(itemPath(workspaceId, id), {
    headers: await headers(),
  });
  if (!res.ok) throw new Error(await errorMessage(res, 'Failed to load file'));
  const wire = (await res.json()) as WorkspaceArtifactWire & {
    shared_with?: string[] | null;
  };
  return { ...fromWire(wire), sharedWith: wire.shared_with ?? null };
}

/**
 * Where a file lives, answered for a bare artifact id — the one question a `?file=` deep link can ask
 * before it knows which workspace to look in. `found` names the workspace; `not_member` says the file
 * exists in a workspace the caller is not in; `missing` says there is no such file for this caller.
 */
export type ArtifactLocation =
  | { outcome: 'found'; artifactId: string; workspaceId: string }
  | { outcome: 'not_member' }
  | { outcome: 'missing' };

type ArtifactLocationWire =
  | { outcome: 'found'; artifact_id: string; workspace_id: string }
  | { outcome: 'not_member' }
  | { outcome: 'missing' };

function locationFromWire(wire: ArtifactLocationWire): ArtifactLocation {
  if (wire.outcome === 'found') {
    return {
      outcome: 'found',
      artifactId: wire.artifact_id,
      workspaceId: wire.workspace_id,
    };
  }
  return { outcome: wire.outcome };
}

/**
 * Resolve which workspace an artifact belongs to. Its own route, `/v1/artifacts/:id/location`, not under
 * {@link API_BASE}: the caller has no workspace to scope by yet. The service always answers 200 with an
 * `outcome`, so any other status — a gateway that does not route it yet, a 5xx — is a transport failure
 * and throws rather than being read as "missing"; the page then behaves as it did before the locator.
 */
export async function locateArtifact(id: string): Promise<ArtifactLocation> {
  const res = await fetch(`/v1/artifacts/${encodeURIComponent(id)}/location`, {
    headers: await headers(),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to locate file'));
  return locationFromWire((await res.json()) as ArtifactLocationWire);
}

/**
 * Largest revision the service will presign, mirroring `MAX_ARTIFACT_REVISION_BYTES` in
 * cti-workspace-service — the same 25 MiB cap knowledge uploads and chat attachments carry. Held here so
 * a caller can refuse an oversized file before its bytes move; the service's own 413 stays the authority.
 */
export const MAX_ARTIFACT_REVISION_BYTES = 25 * 1024 * 1024;

/**
 * What the service signs an upload with when the caller declares no content type. Presigned storage
 * compares the `Content-Type` of the PUT against the signed one, so a caller that lets the service pick
 * the default has to send exactly this value — which is why the sign calls below normalize to it rather
 * than omitting the field and leaving the effective type unknown to the client.
 */
export const DEFAULT_ARTIFACT_CONTENT_TYPE = 'application/octet-stream';

/**
 * A write that lost the optimistic lock: the caller named a `base_revision` that is no longer the head,
 * so someone saved while they were editing. Typed rather than a message the UI has to parse, for the same
 * reason `AttachmentFetchError` is — the caller has to re-read the artifact instead of retrying blindly,
 * and a prose match would break the day the service rewords the sentence.
 */
export class ArtifactRevisionConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ArtifactRevisionConflictError';
  }
}

export class ResourceArchivedError extends Error {
  constructor(message = 'Restore it first') {
    super(message);
    this.name = 'ResourceArchivedError';
  }
}

/** Shown when the service reports a conflict with no body of its own to quote. */
const STALE_REVISION_MESSAGE =
  'Someone saved a new version of this file — re-read it and try again.';

/**
 * Turn a failed write into the right error. The refusals the artifact write routes can answer all mean
 * something a user can act on, so each gets words instead of a raw status: 409 the stale-write conflict
 * (typed, so a caller can branch on it), 413 over the size cap, 403 no write right, 404 gone or never
 * visible. Anything else — 400 for bytes that were never uploaded, 422 for sharing in a personal
 * workspace — keeps the service's own message, which already names what was wrong.
 */
async function throwWriteFailure(
  res: Response,
  fallback: string,
): Promise<never> {
  if (res.status === 409) {
    const text = await res.text().catch(() => '');
    let code: unknown;
    let message = STALE_REVISION_MESSAGE;
    try {
      const body = JSON.parse(text) as { code?: unknown; message?: unknown };
      code = body.code;
      if (typeof body.message === 'string' && body.message.trim()) {
        message = body.message;
      }
    } catch {
      if (text.trim()) message = text.trim();
    }
    if (code === 'resource_archived') throw new ResourceArchivedError(message);
    throw new ArtifactRevisionConflictError(message);
  }
  if (res.status === 413) {
    throw new Error(
      `This file is larger than the ${MAX_ARTIFACT_REVISION_BYTES / (1024 * 1024)} MB limit.`,
    );
  }
  if (res.status === 403)
    throw new Error('You do not have permission to change this file.');
  if (res.status === 404) {
    throw new Error(
      'This file is no longer available — it may have been deleted.',
    );
  }
  throw new Error(await errorMessage(res, fallback));
}

/**
 * One artifact's version history, newest first. Open to anyone who can read the artifact — reading the
 * history is reading. A legacy artifact answers with a single synthesized revision 1, which is a normal
 * one-entry history and not an error.
 */
export async function listArtifactRevisions(
  workspaceId: string,
  id: string,
): Promise<ArtifactRevision[]> {
  const res = await fetch(`${itemPath(workspaceId, id)}/revisions`, {
    headers: await headers(),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load version history'));
  const body = (await res.json()) as { revisions?: ArtifactRevisionWire[] };
  return (body.revisions ?? []).map(revisionFromWire);
}

/** A signed slot for an artifact's next revision: PUT the bytes at `uploadUrl`, then commit `s3Key`. */
export interface ArtifactRevisionTicket {
  uploadUrl: string;
  s3Key: string;
  /** The revision the committed bytes will become — the head plus one. */
  revision: number;
  expiresIn: number;
}

/** A signed slot for a brand-new file: PUT the bytes at `uploadUrl`, then register `s3Key`. */
export interface ArtifactUploadTicket {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

/** The snake_case presign responses. `expires_in` is informational, so a missing one is not an error. */
interface ArtifactTicketWire {
  upload_url: string;
  s3_key: string;
  revision?: number;
  expires_in?: number;
}

/**
 * Ask for a slot for this artifact's next revision (answers 200 — nothing is created yet). `baseRevision`
 * is the revision the caller read, and the service refuses a stale one with a 409 here as well as on the
 * commit. The object key is derived by the service from the artifact and the next revision number, never
 * chosen here.
 */
export async function signArtifactRevision(
  workspaceId: string,
  id: string,
  input: { contentType?: string; size: number; baseRevision: number },
): Promise<ArtifactRevisionTicket> {
  const res = await fetch(`${itemPath(workspaceId, id)}/revisions/sign`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({
      base_revision: input.baseRevision,
      size: input.size,
      content_type: input.contentType || DEFAULT_ARTIFACT_CONTENT_TYPE,
    }),
  });
  if (!res.ok) await throwWriteFailure(res, 'Failed to prepare the save');
  const wire = (await res.json()) as ArtifactTicketWire;
  return {
    uploadUrl: wire.upload_url,
    s3Key: wire.s3_key,
    revision:
      typeof wire.revision === 'number'
        ? wire.revision
        : input.baseRevision + 1,
    expiresIn: typeof wire.expires_in === 'number' ? wire.expires_in : 0,
  };
}

/**
 * Record already-uploaded bytes as the artifact's new head revision (201). This is the authoritative
 * stale-write check — a competing save can land between the presign and here — and the size and content
 * type of the new revision are read from storage, not from this call.
 */
export async function commitArtifactRevision(
  workspaceId: string,
  id: string,
  input: { s3Key: string; baseRevision: number },
): Promise<WorkspaceArtifact> {
  const res = await fetch(`${itemPath(workspaceId, id)}/revisions`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({
      s3_key: input.s3Key,
      base_revision: input.baseRevision,
    }),
  });
  if (!res.ok) await throwWriteFailure(res, 'Failed to save this file');
  return fromWire((await res.json()) as WorkspaceArtifactWire);
}

/**
 * Restore an older revision (201). The service APPENDS a copy of it as a new head — nothing is rolled
 * back and no newer revision is discarded — so the answer's `headRevision` is one past the previous head.
 * `baseRevision` is the head the caller believes they are appending to, so a save that landed while the
 * history was open surfaces as a conflict instead of silently overwriting.
 */
export async function restoreArtifactRevision(
  workspaceId: string,
  id: string,
  revision: number,
  input: { baseRevision: number },
): Promise<WorkspaceArtifact> {
  const res = await fetch(
    `${itemPath(workspaceId, id)}/revisions/${revision}/restore`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({ base_revision: input.baseRevision }),
    },
  );
  if (!res.ok) await throwWriteFailure(res, 'Failed to restore this version');
  return fromWire((await res.json()) as WorkspaceArtifactWire);
}

/**
 * Rename an artifact. The name becomes the filename segment of every future revision key, so the service
 * sanitizes it and answers with the name it actually stored — which is what the caller must display.
 */
export async function renameArtifact(
  workspaceId: string,
  id: string,
  name: string,
): Promise<WorkspaceArtifact> {
  const res = await fetch(itemPath(workspaceId, id), {
    method: 'PATCH',
    headers: await headers(),
    body: JSON.stringify({ name }),
  });
  if (!res.ok) await throwWriteFailure(res, 'Failed to rename this file');
  return fromWire((await res.json()) as WorkspaceArtifactWire);
}

/**
 * Ask for a slot for a brand-new file in the workspace (answers 200). The artifact id lives inside the
 * signed key, which is why the answer carries no id and {@link createArtifact} needs nothing but the key.
 */
export async function signArtifactUpload(
  workspaceId: string,
  input: { filename: string; contentType?: string; size: number },
): Promise<ArtifactUploadTicket> {
  const res = await fetch(`${collectionPath(workspaceId)}/uploads/sign`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({
      filename: input.filename,
      size: input.size,
      content_type: input.contentType || DEFAULT_ARTIFACT_CONTENT_TYPE,
    }),
  });
  if (!res.ok) await throwWriteFailure(res, 'Failed to prepare the upload');
  const wire = (await res.json()) as ArtifactTicketWire;
  return {
    uploadUrl: wire.upload_url,
    s3Key: wire.s3_key,
    expiresIn: typeof wire.expires_in === 'number' ? wire.expires_in : 0,
  };
}

/**
 * Register uploaded bytes as a new artifact (201). It starts private (`restricted`) on revision 1; the
 * declared size and content type are courtesy values the service replaces with what storage reports.
 */
export async function createArtifact(
  workspaceId: string,
  input: { s3Key: string; name: string; contentType?: string; size: number },
): Promise<WorkspaceArtifact> {
  const res = await fetch(collectionPath(workspaceId), {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({
      s3_key: input.s3Key,
      name: input.name,
      size: input.size,
      content_type: input.contentType || DEFAULT_ARTIFACT_CONTENT_TYPE,
    }),
  });
  if (!res.ok) await throwWriteFailure(res, 'Failed to add this file');
  return fromWire((await res.json()) as WorkspaceArtifactWire);
}

/**
 * PUT the bytes straight into storage at a presigned url. No platform bearer: the signature IS the
 * authorisation, and a header the signature does not cover makes storage refuse the request. The content
 * type must be exactly the one that was signed — storage compares them — so it is passed in rather than
 * read off the blob, which may carry a different (or empty) type.
 */
export async function putArtifactBytes(
  uploadUrl: string,
  body: Blob,
  contentType: string,
): Promise<void> {
  const stored = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body,
  });
  if (!stored.ok) {
    throw new Error(
      `Those bytes could not be stored (${stored.status}) — check your connection and try again.`,
    );
  }
}

/**
 * Save new bytes over an existing artifact: presign → PUT → commit. Three calls, one user-visible step,
 * so no screen has to re-assemble them and a failure at any step surfaces as one error with nothing
 * half-recorded (an abandoned upload leaves an object no revision points at, which the service reaps).
 *
 * The content type is resolved once and used for both the signature and the PUT, because storage rejects
 * a PUT whose type differs from the signed one. `baseRevision` is the revision the editor loaded; a
 * {@link ArtifactRevisionConflictError} from either call means someone else saved first.
 */
export async function saveArtifactRevision(
  workspaceId: string,
  id: string,
  input: { body: Blob; contentType?: string; baseRevision: number },
): Promise<WorkspaceArtifact> {
  const contentType =
    input.contentType || input.body.type || DEFAULT_ARTIFACT_CONTENT_TYPE;
  const ticket = await signArtifactRevision(workspaceId, id, {
    contentType,
    size: input.body.size,
    baseRevision: input.baseRevision,
  });
  await putArtifactBytes(ticket.uploadUrl, input.body, contentType);
  return commitArtifactRevision(workspaceId, id, {
    s3Key: ticket.s3Key,
    baseRevision: input.baseRevision,
  });
}

/**
 * Put one local file into the workspace's files: presign → PUT → register. The same three-step shape as
 * {@link saveArtifactRevision}, and the same reason for it. There is no byte progress to report — `fetch`
 * gives none for a request body — so a caller can only show uploading → uploaded/error.
 */
export async function uploadNewArtifact(
  workspaceId: string,
  file: File,
): Promise<WorkspaceArtifact> {
  const contentType = file.type || DEFAULT_ARTIFACT_CONTENT_TYPE;
  const ticket = await signArtifactUpload(workspaceId, {
    filename: file.name,
    contentType,
    size: file.size,
  });
  await putArtifactBytes(ticket.uploadUrl, file, contentType);
  return createArtifact(workspaceId, {
    s3Key: ticket.s3Key,
    name: file.name,
    contentType,
    size: file.size,
  });
}

/**
 * Trim `q` and drop empty filters, in a fixed key order, so the same logical request maps to one query
 * key (e.g. `'foo'` and `' foo '` share a cache entry) rather than several.
 */
function normalizeFilters(
  filters: WorkspaceArtifactFilters,
): WorkspaceArtifactFilters {
  const normalized: WorkspaceArtifactFilters = {};
  const q = filters.q?.trim();
  if (q) normalized.q = q;
  const type = filters.type?.trim();
  if (type) normalized.type = type;
  const conversationId = filters.conversationId?.trim();
  if (conversationId) normalized.conversationId = conversationId;
  if (filters.status === 'archived') normalized.status = 'archived';
  return normalized;
}

/**
 * Query-key factory so mutations can invalidate precisely. Both keys start with the same
 * `['workspace-artifacts', workspaceId]` prefix, which is what {@link invalidateWorkspaceArtifacts}
 * invalidates: `list` carries the filters object, so there is no single list key to name, and a write to
 * one artifact changes its row in every filtered list AND its history.
 */
export const workspaceArtifactKeys = {
  list: (workspaceId: string, filters: WorkspaceArtifactFilters = {}) =>
    ['workspace-artifacts', workspaceId, filters] as const,
  item: (workspaceId: string, id: string) =>
    ['workspace-artifacts', workspaceId, 'item', id] as const,
  revisions: (workspaceId: string, id: string) =>
    ['workspace-artifacts', workspaceId, 'revisions', id] as const,
};

/** Drop every cached read of this workspace's artifacts — the filtered lists and the histories. */
function invalidateWorkspaceArtifacts(
  queryClient: QueryClient,
  workspaceId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: ['workspace-artifacts', workspaceId],
  });
}

/** TanStack Query factory for a workspace's artifact list. Filters are normalized so the query key
 * and the request stay in lockstep. */
export function workspaceArtifactsQueryOptions(
  workspaceId: string,
  filters: WorkspaceArtifactFilters = {},
) {
  const normalized = normalizeFilters(filters);
  return queryOptions({
    queryKey: workspaceArtifactKeys.list(workspaceId, normalized),
    queryFn: () => listWorkspaceArtifacts(workspaceId, normalized),
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
    staleTime: 30_000,
  });
}

/**
 * TanStack Query factory for ONE artifact, addressed by its registry id.
 *
 * The lists answer "which files are here"; this answers "what is this file", which is the only question
 * available when the id arrives from outside any list — a file the agent named as a source in an answer
 * belongs to the workspace, not necessarily to the conversation being read. The service applies the
 * file's own access rules to the caller's session, so an id this reader may not open comes back 404 and
 * the caller shows that rather than substituting another file.
 *
 * Its key sits under the same `['workspace-artifacts', workspaceId]` prefix every write invalidates, so
 * a rename or a new revision refreshes it along with the lists.
 */
export function workspaceArtifactQueryOptions(workspaceId: string, id: string) {
  return queryOptions({
    queryKey: workspaceArtifactKeys.item(workspaceId, id),
    queryFn: () => getWorkspaceArtifact(workspaceId, id),
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * TanStack Query factory for {@link locateArtifact}. No retry: a failure means the route is not there or
 * the service is down, and the page's fallback is better than a spinner that waits for three attempts.
 * No cache either: where a file is and whether the caller may open it is asked once per visit, so a
 * link opened again after the file was deleted or the membership changed gets the current answer, not
 * the one from thirty seconds ago.
 */
export function artifactLocationQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['artifact-location', id] as const,
    queryFn: () => locateArtifact(id),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * TanStack Query factory for one artifact's version history. Newest first, as the service sends it.
 */
export function workspaceArtifactRevisionsQueryOptions(
  workspaceId: string,
  id: string,
) {
  return queryOptions({
    queryKey: workspaceArtifactKeys.revisions(workspaceId, id),
    queryFn: () => listArtifactRevisions(workspaceId, id),
    staleTime: 30_000,
  });
}

export function useDeleteWorkspaceArtifact(
  workspaceId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteWorkspaceArtifact(workspaceId, id),
    onSuccess: () => invalidateWorkspaceArtifacts(qc, workspaceId),
  });
}

/** Change who can read an artifact. Invalidates the list so badges and rows refresh. */
export function useSetArtifactVisibility(
  workspaceId: string,
): UseMutationResult<
  WorkspaceArtifact,
  Error,
  { id: string; visibility: ArtifactVisibility; userIds?: readonly string[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      visibility,
      userIds,
    }: {
      id: string;
      visibility: ArtifactVisibility;
      userIds?: readonly string[];
    }) => setWorkspaceArtifactVisibility(workspaceId, id, visibility, userIds),
    onSuccess: () => invalidateWorkspaceArtifacts(qc, workspaceId),
  });
}

/** What a save needs: which artifact, the bytes, and the revision the caller read before editing. */
export interface SaveArtifactRevisionInput {
  id: string;
  body: Blob;
  contentType?: string;
  baseRevision: number;
}

/** What a restore needs: which artifact, which revision to bring back, and the head it appends to. */
export interface RestoreArtifactRevisionInput {
  id: string;
  revision: number;
  baseRevision: number;
}

/**
 * Save new bytes as an artifact's next revision. Invalidates the whole workspace prefix, because the save
 * moves the head every list row shows AND adds an entry to the history.
 */
export function useSaveArtifactRevision(
  workspaceId: string,
): UseMutationResult<WorkspaceArtifact, Error, SaveArtifactRevisionInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: SaveArtifactRevisionInput) =>
      saveArtifactRevision(workspaceId, id, input),
    onSuccess: () => invalidateWorkspaceArtifacts(qc, workspaceId),
  });
}

/**
 * Rename an artifact. Invalidates the whole workspace prefix rather than one list: the name shows in the
 * Artifacts section's list AND in the chat side panel, which reads the same list filtered by conversation,
 * so a narrower invalidation would leave the old name on screen in the other place.
 */
export function useRenameArtifact(
  workspaceId: string,
): UseMutationResult<WorkspaceArtifact, Error, { id: string; name: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      renameArtifact(workspaceId, id, name),
    onSuccess: () => invalidateWorkspaceArtifacts(qc, workspaceId),
  });
}

/** Put one local file into the workspace's files (presign → PUT → register), then refresh the lists. */
export function useUploadArtifact(
  workspaceId: string,
): UseMutationResult<WorkspaceArtifact, Error, File> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadNewArtifact(workspaceId, file),
    onSuccess: () => invalidateWorkspaceArtifacts(qc, workspaceId),
  });
}

/**
 * Restore an older revision by appending it as a new head. Same invalidation as a save, for the same
 * reason — a restore IS a save, of bytes that were already in the history.
 */
export function useRestoreArtifactRevision(
  workspaceId: string,
): UseMutationResult<WorkspaceArtifact, Error, RestoreArtifactRevisionInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      revision,
      baseRevision,
    }: RestoreArtifactRevisionInput) =>
      restoreArtifactRevision(workspaceId, id, revision, { baseRevision }),
    onSuccess: () => invalidateWorkspaceArtifacts(qc, workspaceId),
  });
}
